import React, { useEffect, useState, useCallback } from "react";
import { adminListCoupons, adminCreateCoupon, adminUpdateCoupon, adminDeleteCoupon } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Power, PowerOff, Tag, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso; }
}

export default function CouponsTab({ password, onUnauth }) {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminListCoupons(password);
      setCoupons(res.coupons || []);
    } catch (err) {
      if (err?.response?.status === 401) { onUnauth(); return; }
      toast.error("Failed to load coupons");
    } finally { setLoading(false); }
  }, [password, onUnauth]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (c) => {
    try {
      const updated = await adminUpdateCoupon(password, c.code, { active: !c.active });
      setCoupons((list) => list.map((x) => x.code === c.code ? { ...x, ...updated } : x));
      toast.success(`${c.code} is now ${updated.active ? "active" : "inactive"}`);
    } catch { toast.error("Update failed"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}?`)) return;
    try {
      await adminDeleteCoupon(password, c.code);
      setCoupons((list) => list.filter((x) => x.code !== c.code));
      toast.success("Coupon deleted");
    } catch { toast.error("Delete failed"); }
  };

  const onCreated = (doc) => {
    setCoupons((list) => [{ ...doc, uses: 0, revenue: 0 }, ...list]);
    setOpenCreate(false);
    toast.success("Coupon created");
  };

  return (
    <>
      <div className="mt-2 flex items-center justify-between">
        <div>
          <h2 className="font-display text-3xl text-white">Coupons</h2>
          <p className="text-sm text-neutral-500">Create promo codes for influencers and campaigns.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="rounded-full border border-white/10 bg-white/5 p-2 hover:bg-white/10"
            aria-label="Refresh"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
          <button
            data-testid={TID.adminCouponAddBtn}
            onClick={() => setOpenCreate(true)}
            className="flex items-center gap-1.5 rounded-full bg-fire px-4 py-2 text-xs font-bold uppercase tracking-widest text-black hover:scale-[1.02]"
          >
            <Plus size={14} /> New Coupon
          </button>
        </div>
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-white/10 bg-neutral-950">
        <div className="overflow-x-auto">
          <table data-testid={TID.adminCouponsTable} className="w-full min-w-[720px] text-left text-sm">
            <thead className="border-y border-white/5 bg-white/5 text-[11px] uppercase tracking-widest text-neutral-400">
              <tr>
                <th className="px-4 py-2">Code</th>
                <th className="px-4 py-2">Label</th>
                <th className="px-4 py-2 text-right">Discount</th>
                <th className="px-4 py-2 text-right">Uses</th>
                <th className="px-4 py-2 text-right">Revenue</th>
                <th className="px-4 py-2">Created</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={8} className="px-4 py-10 text-center text-neutral-500">Loading…</td></tr>)}
              {!loading && coupons.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-neutral-500">No coupons yet.</td></tr>
              )}
              {coupons.map((c) => (
                <tr key={c.code} data-testid={TID.adminCouponRow} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Tag size={14} className="text-[#FFC107]" />
                      <span className="font-mono text-base font-semibold text-white">{c.code}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-neutral-300">{c.label || "—"}</td>
                  <td className="px-4 py-3 text-right font-display text-lg text-[#FF7A00]">-₹{c.discount}</td>
                  <td className="px-4 py-3 text-right text-neutral-300">{c.uses ?? 0}</td>
                  <td className="px-4 py-3 text-right text-neutral-300">₹{(c.revenue ?? 0).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 text-xs text-neutral-500">{fmtDate(c.created_at)}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-md border px-2 py-0.5 text-[11px] uppercase tracking-wider ${
                      c.active ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400" : "border-neutral-700 bg-neutral-800 text-neutral-500"
                    }`}>
                      {c.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        data-testid={TID.adminCouponToggle}
                        onClick={() => toggle(c)}
                        title={c.active ? "Deactivate" : "Activate"}
                        className="rounded-md border border-white/10 bg-white/5 p-1.5 text-neutral-300 hover:bg-white/10"
                      >
                        {c.active ? <PowerOff size={14} /> : <Power size={14} />}
                      </button>
                      <button
                        data-testid={TID.adminCouponDelete}
                        onClick={() => remove(c)}
                        title="Delete"
                        className="rounded-md border border-red-500/30 bg-red-500/10 p-1.5 text-red-400 hover:bg-red-500/20"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <CreateCouponDialog
        open={openCreate}
        onOpenChange={setOpenCreate}
        password={password}
        onCreated={onCreated}
      />
    </>
  );
}

function CreateCouponDialog({ open, onOpenChange, password, onCreated }) {
  const [code, setCode] = useState("");
  const [discount, setDiscount] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setCode(""); setDiscount(""); setLabel(""); } }, [open]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const d = parseInt(discount, 10);
    if (!code.trim()) return toast.error("Enter a code");
    if (!d || d <= 0 || d >= 749) return toast.error("Discount must be 1–748");
    setBusy(true);
    try {
      const doc = await adminCreateCoupon(password, { code: code.trim().toUpperCase(), discount: d, label });
      onCreated(doc);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm border-white/10 bg-neutral-950 text-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">New Coupon</DialogTitle>
          <DialogDescription className="text-neutral-400">
            Flat ₹ discount applied to prepaid or COD orders.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="mt-2 grid gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="c_code">Code</Label>
            <Input
              data-testid={TID.adminCouponCodeInput}
              id="c_code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="INFLUENCER50"
              className="border-white/10 bg-white/5 font-mono uppercase"
              autoFocus
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="c_discount">Discount (₹ flat)</Label>
            <Input
              data-testid={TID.adminCouponDiscountInput}
              id="c_discount"
              type="number"
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
              placeholder="100"
              min={1}
              max={748}
              className="border-white/10 bg-white/5"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="c_label">Label (optional)</Label>
            <Input
              data-testid={TID.adminCouponLabelInput}
              id="c_label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Instagram creator"
              className="border-white/10 bg-white/5"
            />
          </div>
          <button
            data-testid={TID.adminCouponSubmit}
            disabled={busy}
            type="submit"
            className="mt-2 flex items-center justify-center gap-2 rounded-full bg-fire py-3 text-sm font-bold uppercase tracking-widest text-black disabled:opacity-60"
          >
            {busy ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />} Create
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
